import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getVideos } from '../api';
import { getYouTubeId, getThumbnailUrl } from '../utils/youtube';

// 'YYYY-MM-DD' → 'May 2, 2025' (영문 월 이름). Date 객체를 쓰지 않고 문자열을
// 쪼개 포맷해 타임존 밀림을 방지한다. 형식이 아니면 원본을 그대로 반환.
const MONTH_NAMES = [
   'January', 'February', 'March', 'April', 'May', 'June',
   'July', 'August', 'September', 'October', 'November', 'December',
];
const formatPerformedOn = (s) => {
   if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return s || '';
   const [y, m, d] = s.split('-').map(Number);
   const name = MONTH_NAMES[m - 1];
   if (!name) return s;
   return `${name} ${d}, ${y}`;
};

const Videos = () => {
   const [selectedVideo, setSelectedVideo] = useState(null);
   const [videos, setVideos] = useState([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);

   useEffect(() => {
      loadVideos();
   }, []);

   const loadVideos = async () => {
      try {
         setError(null);
         const data = await getVideos();
         setVideos(data);
      } catch (err) {
         console.error('Failed to load videos:', err);
         setError('Failed to load videos. Please try again later.');
      }
      setLoading(false);
   };

   return (
      <div>
         {/* Hero Section */}
         <div style={styles.hero} className="subpage-hero">
            <motion.h1
               initial={{ opacity: 0, y: 30 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.8, delay: 0.3 }}
               style={styles.heroTitle}
            >
               Videos
            </motion.h1>
            <motion.p
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               transition={{ duration: 0.8, delay: 0.6 }}
               style={styles.heroSubtitle}
            >
               Performance Recordings
            </motion.p>
         </div>

         {/* Videos Grid Section */}
         <section style={styles.section}>
            <div style={styles.container}>
               {loading ? (
                  <div style={styles.loading}>Loading...</div>
               ) : error ? (
                  <div style={styles.error}>{error}</div>
               ) : (
                  <div style={styles.videosGrid} className="videos-grid">
                     {videos.map((video, index) => {
                        const ytId = getYouTubeId(video.url);
                        return (
                           <motion.div
                              key={video.id}
                              initial={{ opacity: 0, y: 40 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.6, delay: index * 0.05 }}
                              style={styles.videoCard}
                              className="video-card"
                              onClick={() => setSelectedVideo(video)}
                           >
                              <div style={styles.videoWrapper}>
                                 {ytId ? (
                                    <>
                                       <img
                                          src={getThumbnailUrl(ytId)}
                                          alt={video.title}
                                          loading="lazy"
                                          style={styles.thumbImage}
                                       />
                                       <div style={styles.playOverlay}>
                                          <svg width="64" height="64" viewBox="0 0 64 64" aria-hidden="true">
                                             <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.45)" />
                                             <path d="M26 20 L46 32 L26 44 Z" fill="#ffffff" />
                                          </svg>
                                       </div>
                                    </>
                                 ) : (
                                    <iframe
                                       src={video.url}
                                       title={video.title}
                                       frameBorder="0"
                                       allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                       allowFullScreen
                                       style={styles.iframe}
                                    />
                                 )}
                              </div>
                              <div style={styles.videoContent}>
                                 <h3 style={styles.videoTitle}>{video.title}</h3>
                                 {video.performed_on && (
                                    <p style={styles.videoDate}>{formatPerformedOn(video.performed_on)}</p>
                                 )}
                                 {video.description && (
                                    <p style={styles.videoDescription}>{video.description}</p>
                                 )}
                              </div>
                           </motion.div>
                        );
                     })}
                  </div>
               )}
            </div>
         </section>

         {/* Video Modal */}
         <AnimatePresence>
            {selectedVideo && (
               <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  style={styles.modal}
                  onClick={() => setSelectedVideo(null)}
               >
                  <motion.div
                     initial={{ scale: 0.8, opacity: 0 }}
                     animate={{ scale: 1, opacity: 1 }}
                     exit={{ scale: 0.8, opacity: 0 }}
                     style={styles.modalContent}
                     onClick={(e) => e.stopPropagation()}
                  >
                     <button
                        style={styles.closeButton}
                        onClick={() => setSelectedVideo(null)}
                     >
                        &times;
                     </button>
                     <div style={styles.modalVideo}>
                        <iframe
                           src={selectedVideo.url + (selectedVideo.url.includes('?') ? '&' : '?') + 'autoplay=1'}
                           title={selectedVideo.title}
                           frameBorder="0"
                           allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                           allowFullScreen
                           style={styles.modalIframe}
                        />
                     </div>
                     <div style={styles.modalInfo}>
                        <h2 style={styles.modalTitle}>{selectedVideo.title}</h2>
                        <p style={styles.modalDescription}>{selectedVideo.description}</p>
                     </div>
                  </motion.div>
               </motion.div>
            )}
         </AnimatePresence>
      </div>
   );
};

const styles = {
   hero: {
      position: 'relative',
      height: '50vh',
      minHeight: '400px',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
   },
   heroTitle: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: 'clamp(2.5rem, 8vw, 5rem)',
      fontWeight: 300,
      color: 'white',
      letterSpacing: '0.3em',
      textTransform: 'uppercase',
      margin: 0,
   },
   heroSubtitle: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.9rem',
      letterSpacing: '0.3em',
      textTransform: 'uppercase',
      color: '#c9a227',
      marginTop: '20px',
   },
   section: {
      padding: '100px 0',
      backgroundColor: '#fafafa',
   },
   container: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 5%',
   },
   loading: {
      textAlign: 'center',
      padding: '50px',
      fontFamily: "'Montserrat', sans-serif",
      color: '#888',
   },
   error: {
      textAlign: 'center',
      padding: '50px',
      fontFamily: "'Montserrat', sans-serif",
      color: '#c9a227',
   },
   videosGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 350px), 1fr))',
      gap: '40px',
   },
   videoCard: {
      backgroundColor: 'white',
      overflow: 'hidden',
      borderRadius: '8px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      transition: 'transform 0.4s ease, box-shadow 0.4s ease',
      cursor: 'pointer',
   },
   videoWrapper: {
      position: 'relative',
      paddingTop: '56.25%',
      overflow: 'hidden',
      backgroundColor: '#0a0a0a',
   },
   iframe: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      border: 'none',
   },
   thumbImage: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
   },
   playOverlay: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
   },
   videoContent: {
      padding: '25px 30px',
      borderTop: '1px solid rgba(201, 162, 39, 0.2)',
   },
   category: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.7rem',
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      color: '#c9a227',
      fontWeight: 500,
   },
   videoTitle: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: '1.4rem',
      fontWeight: 500,
      color: '#0a0a0a',
      margin: '10px 0',
      lineHeight: 1.3,
   },
   videoDate: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.75rem',
      fontWeight: 500,
      letterSpacing: '0.08em',
      color: '#c9a227',
      marginBottom: '4px',
   },
   videoDescription: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.85rem',
      fontWeight: 300,
      color: '#666',
      lineHeight: 1.6,
   },
   modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.95)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '20px',
   },
   modalContent: {
      position: 'relative',
      width: '100%',
      maxWidth: '1000px',
      backgroundColor: '#0a0a0a',
   },
   closeButton: {
      position: 'absolute',
      top: '-50px',
      right: '0',
      background: 'none',
      border: 'none',
      color: 'white',
      fontSize: '2.5rem',
      cursor: 'pointer',
      fontWeight: 300,
      lineHeight: 1,
      transition: 'color 0.3s ease',
   },
   modalVideo: {
      position: 'relative',
      paddingTop: '56.25%',
   },
   modalIframe: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      border: 'none',
   },
   modalInfo: {
      padding: '30px',
   },
   modalTitle: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: '1.8rem',
      fontWeight: 400,
      color: 'white',
      marginBottom: '10px',
   },
   modalDescription: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.95rem',
      fontWeight: 300,
      color: '#888',
   },
};

export default Videos;
